import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';
import { formatScore, resolveAssetUrl } from '../../../utilities/BL/PersonalSurfer';

/**
 * COMPONENTE VISUAL: PERSONAL SURFER OVERLAY VIEW
 * ------------------------------------------------------------------
 * Renderiza la interfaz visual del overlay en pantalla gigante (HU-05)
 * Soporta modo 'invitation' (QRs de alto contraste para gradas)
 * y modo 'leaderboard' (Top 10 en tiempo real con medallas y animaciones).
 * Cumple con la regla Zero-Canvas (no renderiza nada en estado idle).
 * ------------------------------------------------------------------
 */
export function PersonalSurfer({
    flowState = 'idle',
    overlayMode = 'invitation',
    qrCount = 1,
    offsetY = 0,
    banner1536Url = '',
    banner1920Url = '',
    qrCenterLogoUrl = '',
    leaderboard = [],
    activeTheme = null,
    roomId = ''
}) {
    const [windowDimensions, setWindowDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080
    });

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setWindowDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, []);

    // REGLA ZERO-CANVAS: No renderizar nada si está en estado 'idle'
    if (flowState === 'idle') return null;

    // URL dinámica para el QR apuntando a la webapp móvil
    const qrValue = typeof window !== 'undefined'
        ? `${window.location.origin}/psl-surfer?source=qr_scan${roomId ? `&roomId=${encodeURIComponent(roomId)}` : ''}`
        : '';

    const mdSrc = banner1536Url
        ? resolveAssetUrl(banner1536Url)
        : (activeTheme?.banner1536Url ? resolveAssetUrl(activeTheme.banner1536Url) : '/img/overlays/rubaquiz/cintillo-ruba-1536-x-180-px.png');

    const lgSrc = banner1920Url
        ? resolveAssetUrl(banner1920Url)
        : (activeTheme?.banner1920Url ? resolveAssetUrl(activeTheme.banner1920Url) : '/img/overlays/rubaquiz/cintillo-ruba-1920-x-180-px.png');

    const centerLogo = qrCenterLogoUrl
        ? resolveAssetUrl(qrCenterLogoUrl)
        : (activeTheme?.qrCenterLogoUrl ? resolveAssetUrl(activeTheme.qrCenterLogoUrl) : '/img/overlays/rubaquiz/interaktia-transparent.png');

    const isDoubleQr = Number(qrCount) === 2;
    const isLeaderboardMode = String(overlayMode).toLowerCase() === 'leaderboard';

    const qrImageSettings = centerLogo ? {
        src: centerLogo,
        x: undefined,
        y: undefined,
        height: 52,
        width: 52,
        excavate: true
    } : null;

    const themePrimary = activeTheme?.primaryColor || '#00e5ff';
    const themeAccent = activeTheme?.accentColor || '#ff007f';

    const renderMedal = (position) => {
        if (position === 1) return <span className="personal-surfer-overlay__medal personal-surfer-overlay__medal--gold">🥇</span>;
        if (position === 2) return <span className="personal-surfer-overlay__medal personal-surfer-overlay__medal--silver">🥈</span>;
        if (position === 3) return <span className="personal-surfer-overlay__medal personal-surfer-overlay__medal--bronze">🥉</span>;
        return <span className="personal-surfer-overlay__pos-number">#{position}</span>;
    };

    return (
        <>
            {/* Confetti cuando hay jugadores en el podio de Leaderboard */}
            {isLeaderboardMode && leaderboard.length > 0 && (
                <Confetti
                    width={windowDimensions.width}
                    height={windowDimensions.height}
                    numberOfPieces={120}
                    recycle={true}
                    gravity={0.12}
                    colors={[themePrimary, themeAccent, '#ffd700', '#ffffff', '#00ff88', '#ff9900']}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 9998,
                        pointerEvents: 'none'
                    }}
                />
            )}

            <div
                className="personal-surfer-overlay"
                style={{ bottom: `${offsetY}px` }}
            >
                <div className={`personal-surfer-overlay__banner personal-surfer-overlay__banner--${flowState}`}>
                    {/* Banners de fondo para resoluciones 1536px y 1920px */}
                    <img
                        src={mdSrc}
                        className="personal-surfer-overlay__banner__Image--Md"
                        alt="Esquivador Personal"
                    />
                    <img
                        src={lgSrc}
                        className="personal-surfer-overlay__banner__Image--Lg"
                        alt="Esquivador Personal Lg"
                    />

                    {/* MODO INVITACIÓN: QRs de alto contraste y llamado a la acción */}
                    {!isLeaderboardMode && (
                        <div className="personal-surfer-overlay__invitation-content">
                            <div className="personal-surfer-overlay__invitation-title">
                                <span className="personal-surfer-overlay__invitation-badge">
                                    ¡JUEGA AHORA EN TU CELULAR!
                                </span>
                                <h2 className="personal-surfer-overlay__invitation-heading">
                                    ¡ESCANEA EL QR Y SURFEA HACIA EL RÉCORD!
                                </h2>
                            </div>

                            {/* Códigos QR (1 o 2) */}
                            {isDoubleQr ? (
                                <>
                                    <div className="personal-surfer-overlay__qr-box personal-surfer-overlay__qr-box--left">
                                        <QRCodeSVG
                                            value={qrValue}
                                            size={256}
                                            style={{ width: '92%', height: '92%', display: 'block' }}
                                            bgColor="#ffffff"
                                            fgColor="#000000"
                                            level="H"
                                            imageSettings={qrImageSettings || undefined}
                                        />
                                    </div>

                                    <div className="personal-surfer-overlay__qr-box personal-surfer-overlay__qr-box--right">
                                        <QRCodeSVG
                                            value={qrValue}
                                            size={256}
                                            style={{ width: '92%', height: '92%', display: 'block' }}
                                            bgColor="#ffffff"
                                            fgColor="#000000"
                                            level="H"
                                            imageSettings={qrImageSettings || undefined}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="personal-surfer-overlay__qr-box personal-surfer-overlay__qr-box--single">
                                    <QRCodeSVG
                                        value={qrValue}
                                        size={256}
                                        style={{ width: '92%', height: '92%', display: 'block' }}
                                        bgColor="#ffffff"
                                        fgColor="#000000"
                                        level="H"
                                        imageSettings={qrImageSettings || undefined}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* MODO LEADERBOARD: Top 10 en vivo */}
                    {isLeaderboardMode && (
                        <div className="personal-surfer-overlay__leaderboard-wrapper">
                            <div className="personal-surfer-overlay__leaderboard-header">
                                <div className="personal-surfer-overlay__leaderboard-title">
                                    <i className="bi bi-trophy-fill" style={{ color: '#ffd700', marginRight: 8 }} />
                                    <span>TOP 10 • RÉCORD DEL ESTADIO</span>
                                </div>
                                <div className="personal-surfer-overlay__leaderboard-qr-mini">
                                    <span className="personal-surfer-overlay__qr-mini-label">¡Escanea y compite!</span>
                                    <QRCodeSVG
                                        value={qrValue}
                                        size={90}
                                        bgColor="#ffffff"
                                        fgColor="#000000"
                                        level="M"
                                    />
                                </div>
                            </div>

                            <div className="personal-surfer-overlay__leaderboard-grid">
                                {leaderboard.length === 0 ? (
                                    <div className="personal-surfer-overlay__empty">
                                        <span>¡Sé el primero en esquivar y alcanzar el Top 10!</span>
                                    </div>
                                ) : (
                                    leaderboard.slice(0, 10).map((player, idx) => {
                                        const pos = player.rankingPosition || player.position || (idx + 1);
                                        const isTop3 = pos <= 3;
                                        return (
                                            <div
                                                key={player.id || player.participantId || idx}
                                                className={`personal-surfer-overlay__row ${isTop3 ? `personal-surfer-overlay__row--top${pos}` : ''}`}
                                            >
                                                <div className="personal-surfer-overlay__row-pos">
                                                    {renderMedal(pos)}
                                                </div>
                                                <div className="personal-surfer-overlay__row-name">
                                                    {player.participantName || player.name || `Jugador #${pos}`}
                                                </div>
                                                <div className="personal-surfer-overlay__row-score">
                                                    <span className="personal-surfer-overlay__score-value">
                                                        {formatScore(player.score || 0)}
                                                    </span>
                                                    <span className="personal-surfer-overlay__score-unit">pts</span>
                                                </div>
                                                <div className="personal-surfer-overlay__row-time">
                                                    ⏱️ {player.timeSpentSeconds || player.survivalTimeSeconds || 0}s
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

PersonalSurfer.propTypes = {
    flowState: PropTypes.oneOf(['idle', 'entering', 'visible', 'fadingOut']).isRequired,
    overlayMode: PropTypes.oneOf(['invitation', 'leaderboard']),
    qrCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    offsetY: PropTypes.number,
    banner1536Url: PropTypes.string,
    banner1920Url: PropTypes.string,
    qrCenterLogoUrl: PropTypes.string,
    leaderboard: PropTypes.arrayOf(PropTypes.object),
    activeTheme: PropTypes.object,
    roomId: PropTypes.string
};

export default PersonalSurfer;
